/**
 * 需求 R148：報價單品項的「敏感價格已變更」提醒（「$」）判定。
 *
 * PM 2026-08-31（第二則留言，取代同日第一則與「本案暫緩」）：
 *   「重新回應，請忽略 Q11，以本次回答為主：
 *     Q1 比較基準時間：(B) 最後儲存時間 / Q2 欄位範圍：(A) 三類任一有紀錄就亮 /
 *     Q5 權限：(C) 都不限（但因為不會顯示明細，只有提醒作用）/ Q6 互動：(A) 純提示不可點 /
 *     Q7 匯出：(A) PDF／Excel 不顯示 / Q8 舊單查不到產品的列：(A) 靜默不顯示 /
 *     Q9 建立頁：(A) 不需要「$」。」
 *
 * ── 判定規則（單一真相，本檔是前端唯一入口）
 *   有「$」⇔ 該列對應的 Product.auditLog 之中，存在一筆
 *            ① field 命中四條敏感欄位規則（工廠成本…(箱價)／經銷成本…(箱價)／採購成本(單價)／
 *               材積(CUFT)），
 *            ② 且 changedAt 晚於「本報價單的最後儲存時間」（Q1=B）。
 *
 * ── 需求 R164（PM 2026-09-07 逐字留言）
 *   「任務解讀方向錯誤，需求是：若材積變動，成本就會變動，這樣『＄』符號也要顯示」
 *   ＝ 追蹤範圍由 3 類擴張為 4 類，新增「材積(CUFT)」。理由是材積直接進報價單單價公式
 *   （材積係數 × 材積 ÷ 箱入數，一般 16／冷凍 23，見 utils/quoteUnitPrice.js calcTierPrice），
 *   材積一改，這張單算出來的成本就跟著變 → 必須提醒業務重新確認售價。
 *    這條規則同時存在四處（本檔／server utils/sensitivePriceAudit.js／
 *      ProductDetailPage.renderAudit／ProductModal.renderAudit）＋ cleanAuditLog.shouldKeep，
 *      必須逐字同步，詳見下段與 tests/volumeCostAudit.fixture.js。
 *
 * ── 為什麼這裡還是有一份欄位規則（PM Q2「不用重複列清單」的落地說明）
 *   全專案原本恰好三處判別式：ProductDetailPage.renderAudit／ProductModal.renderAudit／
 *   src/server/scripts/cleanAuditLog.js 的 shouldKeep。這三處被
 *   tests/priceAuditScope.fixture.js **逐字**鎖住（G1／G2），改成 import 共用模組
 *   會讓該檔紅燈（AC1 要求它維持全綠）；且其中一處在 server 端，與前端本來就不可共用模組。
 *   → 本票能做到的「不重複列清單」是：**前端新增的這一份是唯一一份新的，且不得與既有三處走鐘**。
 *   tests/sensitivePriceAlertEntry.fixture.js [9] 段會把本函式的函式體與
 *   ProductDetailPage 顯示過濾器的 callback 體做「去註解／去空白後逐字相同」比對，
 *   任何一邊被改動而另一邊沒跟上就立刻紅燈——用鎖取代人自律。
 *   （真正把四份收斂成一份要連 需求 R146 的鎖一起改，屬另案。）
 */

/**
 * 是否為「敏感價格」稽核紀錄。
 *  函式體必須與 ProductDetailPage.js / ProductModal.js 的 allLog.filter callback 逐字相同
 *    （由 需求 R148 fixture [9] 段強制比對），修改前請先看上面的說明。
 */
export function isSensitivePriceAuditEntry(entry) {
  const field = entry.field || '';
  const isCostBox = (field.startsWith('工廠成本') || field.startsWith('經銷成本')) && field.endsWith('(箱價)');
  const isPurchaseUnit = field === '採購成本(單價)';
  const isVolume = field === '材積(CUFT)';
  return isCostBox || isPurchaseUnit || isVolume;
}

/**
 * 這個產品在 sinceDate 之後有沒有敏感價格變更紀錄。
 *
 * @param {object|null|undefined} product 產品實時資料（GET /api/products/:id 的回應，含 auditLog）
 * @param {string|Date|null|undefined} sinceDate 基準時間＝報價單最後儲存時間（Q1=B）
 * @returns {boolean}
 *
 * 容錯（Q8=A「靜默不顯示」）：product 為空（舊單 items.product 為 null 或產品已被硬刪除）、
 * auditLog 不存在、sinceDate 取不到、changedAt 不是合法時間 → 一律回 false，不拋錯、不影響同單其他列。
 */
export function hasSensitivePriceChange(product, sinceDate) {
  if (!product || !Array.isArray(product.auditLog) || product.auditLog.length === 0) return false;
  const since = sinceDate ? new Date(sinceDate).getTime() : NaN;
  if (Number.isNaN(since)) return false;
  return product.auditLog.some((entry) => {
    if (!entry || !isSensitivePriceAuditEntry(entry)) return false;
    const changedAt = new Date(entry.changedAt).getTime();
    return !Number.isNaN(changedAt) && changedAt > since;
  });
}

/**
 * 「$」的 tooltip 文案。
 * Q5=C（都不限）＋ PM 附註「因為不會顯示明細，只有提醒作用」：
 *   對所有進得了該畫面的角色顯示**同一句**通用提醒，
 *   **不列出**欄位名／修改前後值／修改人／時間（＝不顯示明細，也不夾帶任何金額）。
 */
export const SENSITIVE_PRICE_ALERT_TITLE = '此品項的成本在本報價單最後儲存後曾被異動，請確認售價';
